/**
 * CookiePolicy.tsx — Public cookie policy.
 * Rebuilt in Pass 7.
 */

import { LegalPageLayout, Section, Bullets, A, C, F } from './LegalPageLayout';

type CookieRow = { name: string; purpose: string; duration: string };

const COOKIES: CookieRow[] = [
  { name: 'Mode preference', purpose: 'Stores your Admin/Demo mode preference', duration: 'Persistent (localStorage)' },
  { name: 'Authentication', purpose: 'Session token to keep you logged in securely', duration: 'Session (sessionStorage)' },
  { name: 'Analytics', purpose: 'Anonymized usage statistics to improve the platform', duration: '12 months' },
  { name: 'Error tracking', purpose: 'Captures application errors to help us identify and fix issues', duration: 'Session' },
];

export default function CookiePolicy() {
  return (
    <LegalPageLayout pageTitle="Cookie Policy" lastUpdated="April 2026">
      <Section title="1. What are cookies">
        Cookies are small text files stored on your device when you visit a website.
        They help the site remember your preferences and understand how you use the
        service.
      </Section>

      <Section title="2. Cookies we use">
        <CookieTable rows={COOKIES} />
      </Section>

      <Section title="3. How to control cookies">
        You can control cookies through your browser settings. Most browsers allow you
        to block or delete cookies. However, disabling cookies may affect platform
        functionality — for example, the Admin/Demo mode toggle uses localStorage to
        persist your preference.

        <div style={{ marginTop: 16 }}>
          Common browser settings:
          <Bullets items={[
            { label: 'Chrome', text: 'Settings → Privacy and security → Cookies' },
            { label: 'Firefox', text: 'Settings → Privacy & Security → Cookies' },
            { label: 'Safari', text: 'Preferences → Privacy → Manage Website Data' },
            { label: 'Edge', text: 'Settings → Cookies and site permissions' },
          ]} />
        </div>
      </Section>

      <Section title="4. Contact">
        For questions about our use of cookies, contact us at{' '}
        <A href="mailto:admin@meridianregistry.com">admin@meridianregistry.com</A>.
      </Section>
    </LegalPageLayout>
  );
}

function CookieTable({ rows }: { rows: CookieRow[] }) {
  return (
    <div style={{
      background: C.bgPanel,
      border: `1px solid ${C.borderDefault}`,
      borderRadius: 10,
      overflow: 'hidden',
      marginTop: 16,
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {['Cookie', 'Purpose', 'Duration'].map(h => (
              <th key={h} style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 11, fontWeight: 600,
                color: C.cyan,
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                borderBottom: `1px solid ${C.borderDefault}`,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.name} style={{
              borderBottom: i < rows.length - 1 ? `1px solid rgba(148,163,184,0.05)` : 'none',
            }}>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>
                <code style={{
                  fontFamily: F.ui === "'DM Sans', -apple-system, system-ui, sans-serif"
                    ? "'Space Mono', ui-monospace, monospace"
                    : F.ui,
                  fontSize: 13,
                  color: C.textPrimary,
                }}>{row.name}</code>
              </td>
              <td style={{ padding: '12px 16px', fontSize: 14, color: C.textSecondary }}>{row.purpose}</td>
              <td style={{ padding: '12px 16px', fontSize: 14, color: C.textSecondary }}>{row.duration}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
